/* eslint-disable react/no-danger */
/*
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {}

interface OwnProps extends WidgetProps {
    fields: {};
}

type Props = OwnProps & PageProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    location: getLocation(state),
});

export interface StickyHeaderStyles {
    gridItem?: GridItemProps;
}

const stickyHeaderStyles: StickyHeaderStyles = {};

const abrevHeaderStyles = {
    parentContainer: {
        css: css`
            width: 100%;
            position: fixed;
            background-color: black;
            top: 0;
            box-shadow: 0px 3px 6px #00000029;
        `,
    },
    logoWrapper: {
        css: css`
            display: flex;
            flex: 0 0 0%;
            text-align: left;
            padding: 8px 40px 0 0;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    mainNav: {
        css: css`
            display: flex;
            flex: 0 2 42%;
            align-items: center;
            margin-left: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            flex: 0 2 35%;
                        `,
                        css`
                            flex: 0 2 45%;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    searchMenuWrapper: {
        css: css`
            min-width: 320px;
            display: flex;
            align-items: center;
            flex: 0 1 20%;
            padding: 0 0 0 0;
            margin-left: auto;
            margin-right: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkListHidden: {
        below: "lg",
    },
    signInCartContainer: {
        css: css`
            & ${GridOffset} {
                width: 100%;
                margin: 0;
                justify-content: flex-end;
            }
        `,
    },
    headerGridItemContainer: {
        css: css`
            padding-top: 5px !important;
            padding-bottom: 5px !important;
            padding-left: 0;
            padding-right: 0;
            text-align: center;
        `,
    },
    signInGridItemContainer: {
        css: css`
            padding-top: 5px !important;
            padding-bottom: 5px !important;
            padding-left: 25px;
            padding-right: 0;
            text-align: end;
            flex-basis: 41.666667%;
            max-width: 41.666667%;
        `,
    },

    cartGridItemContainer: {
        css: css`
            flex-basis: 41.666667%;
            max-width: 41.666667%;
            padding-top: 5px !important;
            padding-bottom: 5px !important;
            padding-left: 0;
            padding-right: 0;
            text-align: end;
        `,
    },
    searchContainer: {
        css: css`
            flex: 0 0 100%;
            width: 100%;
        `,
    },
    container: {
        css: css`
            display: flex;
            max-width: 1700px;
            margin: auto;
            padding: 10px 0;
        `,
    },
    postHeader: {
        css: css`
            background-color: ${getColor("primary.main")};
            height: 10px;
        `,
    },
};

const StickyHeaderWidget: React.FC<Props> = ({ id, location }) => {
    const [showHeader, setScroll] = useState(false);
    const isContentAdmin: boolean = location.pathname.indexOf("/Content/Page/") >= 0;
    if (isContentAdmin) {
        abrevHeaderStyles.parentContainer = {
            css: css`
                width: 100%;
                position: relative;
                background-color: black;
                box-shadow: 0px 3px 6px #00000029;
            `,
        };
    } else {
        abrevHeaderStyles.parentContainer = {
            css: css`
                width: 100%;
                position: fixed;
                background-color: black;
                top: 0;
                box-shadow: 0px 3px 6px #00000029;
                z-index: 999999;
            `,
        };
    }

    useEffect(() => {
        document.addEventListener("scroll", () => {
            const scrollCheck = window.scrollY > 250;
            if (scrollCheck !== showHeader) {
                setScroll(scrollCheck);
            }
        });
    });

    return (
        <StyledWrapper {...abrevHeaderStyles.parentContainer}>
            {(showHeader || isContentAdmin) && (
                <StyledWrapper>
                    <StyledWrapper {...abrevHeaderStyles.container}>
                        <StyledWrapper {...abrevHeaderStyles.logoWrapper}>
                            <Zone contentId={id} zoneName="AbrevLogo" />
                        </StyledWrapper>
                        <StyledWrapper {...abrevHeaderStyles.mainNav}>
                            <Zone contentId={id} zoneName="AbrevSearch" />
                        </StyledWrapper>
                        <StyledWrapper {...abrevHeaderStyles.searchMenuWrapper}>
                            <GridContainer {...abrevHeaderStyles.signInCartContainer}>
                                <GridItem width={3} {...abrevHeaderStyles.headerGridItemContainer}>
                                    <Zone contentId={id} zoneName="AbrevSupport" />
                                </GridItem>
                                <GridItem width={3} {...abrevHeaderStyles.headerGridItemContainer}>
                                    <Zone contentId={id} zoneName="AbrevQuickOrder" />
                                </GridItem>
                                <GridItem width={3} {...abrevHeaderStyles.headerGridItemContainer}>
                                    <Zone contentId={id} zoneName="AbrevCart" />
                                </GridItem>
                                <GridItem width={3} {...abrevHeaderStyles.headerGridItemContainer}>
                                    <Zone contentId={id} zoneName="AbrevSignIn" />
                                </GridItem>
                            </GridContainer>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...abrevHeaderStyles.postHeader} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Header",
    displayName: "Sticky Header",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(StickyHeaderWidget),
    definition,
};

export default widgetModule;
